import { useMutation, useQueryClient } from '@tanstack/react-query';
import { archiveBoard, type ArchiveBoardRequest } from '@/features/class/boardsApi';
import { useAuthStore, type RawAuthUser } from '@/store/authStore';

/**
 * Remove an archived board from the auth-store's `rawUser` payload so the
 * Sidebar/profile (which read owned classes from the login response, not
 * React Query) drop the class from the active list immediately — archived
 * classes live under Settings → Archived classes, not the main list.
 *
 * Mirrors the helper in `useDeleteBoard.ts`: it keeps both the legacy flat
 * `board[]` and the modern `boardWithCategory[]` shapes consistent.
 */
function removeBoardFromRawUser(boardId: number | string): void {
  const { rawUser, setRawUser } = useAuthStore.getState();
  if (!rawUser) return;

  const targetId = String(boardId);
  const base: RawAuthUser = { ...rawUser };

  const matchesTarget = (entry: unknown): boolean =>
    Boolean(
      entry &&
      typeof entry === 'object' &&
      String((entry as { id?: number | string }).id) === targetId,
    );

  // 1) Legacy flat `board[]`.
  if (Array.isArray(base.board)) {
    base.board = (base.board as unknown[]).filter((entry) => !matchesTarget(entry));
  }

  // 2) Modern `boardWithCategory[]` — drop the row from every group.
  if (Array.isArray(base.boardWithCategory)) {
    base.boardWithCategory = (base.boardWithCategory as unknown[]).map((group) => {
      if (
        group &&
        typeof group === 'object' &&
        Array.isArray((group as Record<string, unknown>).boards)
      ) {
        const typedGroup = group as Record<string, unknown>;
        return {
          ...typedGroup,
          boards: (typedGroup.boards as unknown[]).filter((entry) => !matchesTarget(entry)),
        };
      }
      return group;
    });
  }

  setRawUser(base);
}

/**
 * PUT /midl/api/v3/archive-board/<boardId> — archives a class (board).
 *
 * Wired from `ClassFormModal` so the "Archive class" action persists to the
 * real backend. On success we also patch the auth-store `rawUser` so the
 * sidebar/profile (login-payload driven) and the React-Query joined-boards
 * cache all drop the class from the active list without a hard reload.
 */
export function useArchiveBoardMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, ArchiveBoardRequest>({
    mutationKey: ['class', 'board', 'archive'],
    mutationFn: (vars) => archiveBoard(vars),
    onSuccess: (_data, vars) => {
      removeBoardFromRawUser(vars.boardId);
      // Refresh server-driven sidebar/class lists too.
      void queryClient.invalidateQueries({ queryKey: ['boards', 'joined'] });
    },
  });
}
